import React, { useState, useEffect } from 'react';
import { SectionTitle, Button, ScrollNavigation } from './Components';
import { useApp, User, CURRENCIES, getTodayDateString, isUserDisabled } from './AppContext';
import { ParticipantStatusBadge } from './useActiveCallStatus';

// Declare Stripe/PayPal globals to avoid TS errors without full types
declare const Stripe: any;
declare const paypal: any;

const BookingModal = ({ doctor, onClose, onConfirm }: { doctor: User, onClose: () => void, onConfirm: (date: string, time: string, timezone: string, isFree?: boolean, paymentInfo?: any) => Promise<void> }) => {
  const { user, globalSettings, createPaymentIntent, allAppointments, appointments, formatDoctorPrice, loginGuest, updateUserProfile } = useApp();
  const [date, setDate] = useState(getTodayDateString());
  const [selectedSlot, setSelectedSlot] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<'date' | 'payment'>('date');
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const doctorTimezone = doctor.timezone || 'UTC';

  const priceInfo = formatDoctorPrice(doctor.sessionRate);

  const isGuestOrAnonymous = !user || user.isAnonymous || !user.email || user.email.includes('guest');
  const [patientName, setPatientName] = useState(user?.name && user.name !== 'Guest Patient' ? user.name : '');
  const [patientEmail, setPatientEmail] = useState(user?.email && !user.email.includes('guest') ? user.email : '');
  const [patientPhone, setPatientPhone] = useState(user?.phone || '');

  // Payment State
  const [stripeElement, setStripeElement] = useState<any>(null);
  const [stripe, setStripe] = useState<any>(null);
  const [paymentError, setPaymentError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'stripe' | 'paypal'>('stripe');

  // --- Dynamic Script Loading ---
  useEffect(() => {
    if (step === 'payment' && globalSettings.paymentsEnabled) {
        // Load Stripe
        if (globalSettings.stripeEnabled && !window.document.getElementById('stripe-js')) {
            const script = document.createElement('script');
            script.id = 'stripe-js';
            script.src = 'https://js.stripe.com/v3/';
            script.onload = () => {
                if ((window as any).Stripe && globalSettings.stripePublicKey) {
                    setStripe((window as any).Stripe(globalSettings.stripePublicKey));
                }
            };
            document.body.appendChild(script);
        } else if ((window as any).Stripe && globalSettings.stripePublicKey) {
             setStripe((window as any).Stripe(globalSettings.stripePublicKey));
        }

        // Load PayPal
        if (globalSettings.paypalEnabled && !window.document.getElementById('paypal-js')) {
            const script = document.createElement('script');
            script.id = 'paypal-js';
            script.src = `https://www.paypal.com/sdk/js?client-id=${globalSettings.paypalClientId}&currency=${globalSettings.platformCurrency}`;
            script.onload = () => {
                // PayPal buttons render logic handled in effect below
            };
            document.body.appendChild(script);
        }
    }
  }, [step, globalSettings]);

  // --- Stripe Element Initialization ---
  useEffect(() => {
    if (stripe && step === 'payment' && paymentMethod === 'stripe' && !stripeElement) {
        const initStripe = async () => {
            const amount = globalSettings.paymentsEnabled ? (doctor.sessionRate || 50) : 0;
            const clientSecret = await createPaymentIntent(amount * 100); 
            const elements = stripe.elements({ clientSecret, appearance: { theme: 'stripe' } });
            const cardElement = elements.create('payment');
            cardElement.mount('#card-element');
            setStripeElement(elements);
        };
        initStripe();
    }
  }, [stripe, step, paymentMethod]);

  // --- PayPal Button Render ---
  useEffect(() => {
      if (step === 'payment' && paymentMethod === 'paypal' && (window as any).paypal) {
          const container = document.getElementById('paypal-button-container');
          if (container) container.innerHTML = ''; // Clear previous
          
          const amount = globalSettings.paymentsEnabled ? (doctor.sessionRate || 50) : 0;

          (window as any).paypal.Buttons({
              createOrder: (data: any, actions: any) => {
                  return actions.order.create({
                      purchase_units: [{ amount: { value: amount.toFixed(2) } }]
                  });
              },
              onApprove: async (data: any, actions: any) => {
                  await actions.order.capture();
                  await finalizeBooking();
              },
              onError: (err: any) => setPaymentError("PayPal Transaction Failed")
          }).render('#paypal-button-container');
      }
  }, [step, paymentMethod]);

  const normalizeDate = (d: string) => {
      if (!d) return '';
      const parts = d.trim().split('-');
      if (parts.length === 3) {
        return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
      }
      return d.trim();
  };

  const normalizeTime = (t: string) => {
      if (!t) return '';
      const parts = t.trim().split(':');
      if (parts.length === 2) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
      }
      return t.trim();
  };

  const getDayName = (d: string) => {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      if (!d) return 'Monday';
      const parts = d.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const dateObj = new Date(year, month, day);
        return days[dateObj.getDay()];
      }
      return 'Monday';
  };

  const dayName = getDayName(date);
  const schedule = doctor.workingHours?.[dayName];
  const isOff = schedule && schedule.active === false;
  const allowSpecialRequests = schedule?.allowSpecialRequests ?? doctor.allowSpecialRequests ?? false;

  const normSelectedDate = normalizeDate(date);

  // Booked appointments for this doctor on selected date
  const bookedSlots = React.useMemo(() => {
    const set = new Set<string>();
    const list = allAppointments.length > 0 ? allAppointments : appointments;
    list.forEach(app => {
      if (
        app.doctorId === doctor.id && 
        normalizeDate(app.date) === normSelectedDate && 
        app.status !== 'cancelled'
      ) {
        set.add(normalizeTime(app.time));
      }
    });
    return set;
  }, [allAppointments, appointments, doctor.id, normSelectedDate]);

  // Custom open slots set by doctor for this date (e.g. on non-working days)
  const customOpenSlots = React.useMemo(() => {
    const set = new Set<string>();
    if (doctor.busySlots && Array.isArray(doctor.busySlots)) {
      doctor.busySlots.forEach(b => {
        if (normalizeDate(b.date) === normSelectedDate && b.type === 'open') {
          set.add(normalizeTime(b.time));
        }
      });
    }
    return set;
  }, [doctor.busySlots, normSelectedDate]);

  // Busy/blocked slots set by the doctor
  const busySlots = React.useMemo(() => {
    const set = new Set<string>();
    if (doctor.busySlots && Array.isArray(doctor.busySlots)) {
      doctor.busySlots.forEach(b => {
        if (normalizeDate(b.date) === normSelectedDate && (b.type === 'busy' || !b.type)) {
          set.add(normalizeTime(b.time));
        }
      });
    }
    return set;
  }, [doctor.busySlots, normSelectedDate]);

  const timeSlots = React.useMemo(() => {
    if (isOff) {
      if (customOpenSlots.size > 0) {
        return Array.from(customOpenSlots).sort();
      }
      if (!allowSpecialRequests) {
        return [];
      }
    }
    const startHour = schedule && schedule.start ? parseInt(schedule.start.split(':')[0]) : 9;
    const endHour = schedule && schedule.end ? parseInt(schedule.end.split(':')[0]) : 17;
    const slots: string[] = [];
    for (let i = startHour; i < endHour; i++) {
        const hourStr = i.toString().padStart(2, '0');
        slots.push(`${hourStr}:00`);
        slots.push(`${hourStr}:30`);
    }
    customOpenSlots.forEach(s => {
      if (!slots.includes(s)) slots.push(s);
    });
    return slots.sort();
  }, [schedule, isOff, allowSpecialRequests, customOpenSlots]);

  const handleNext = () => {
      setPaymentError('');
      if (!selectedSlot) {
          setPaymentError("Please choose a time slot for your consultation.");
          return;
      }
      if (isGuestOrAnonymous) {
          if (!patientName.trim()) {
              setPaymentError("Please enter your name so your physician knows who they are consulting with.");
              return;
          }
          if (!patientEmail.trim() || !patientEmail.includes('@') || !patientEmail.includes('.')) {
              setPaymentError("Please enter a valid email address so we can deliver your appointment confirmation and video consultation link.");
              return;
          }
      }
      if (globalSettings.paymentsEnabled && !priceInfo.isFree) {
          setStep('payment');
      } else if (globalSettings.complimentaryAccessEnabled || priceInfo.isFree) {
          finalizeBooking();
      } else {
          setPaymentError("Scheduling is currently restricted by administration. Please try again later.");
      }
  };

  const isSubmittingRef = React.useRef(false);

  const finalizeBooking = async () => {
    if (isSubmittingRef.current || isProcessing) return;
    isSubmittingRef.current = true;
    setIsProcessing(true);
    setPaymentError('');
    try {
        if (!user) {
            await loginGuest();
        }
        if (patientName.trim() || patientEmail.trim()) {
            try {
                await updateUserProfile({
                    name: patientName.trim() || user?.name || 'Patient',
                    email: patientEmail.trim() || user?.email,
                    phone: patientPhone.trim() || user?.phone
                });
            } catch (_) {}
        }
        const paymentInfo = {
            feeAmount: priceInfo.rawDisplayAmount,
            feeCurrency: priceInfo.currencyCode,
            paymentStatus: priceInfo.isFree ? ('complimentary' as const) : ('paid' as const),
            patientName: patientName.trim(),
            patientEmail: patientEmail.trim(),
            patientPhone: patientPhone.trim()
        };
        await onConfirm(date, selectedSlot, doctorTimezone, priceInfo.isFree, paymentInfo);
    } catch (e: any) {
        isSubmittingRef.current = false;
        setPaymentError(e.message || "Failed to finalize appointment. Please try again or select another slot.");
    } finally {
        setIsProcessing(false);
    }
  };

  const handleStripePay = async () => {
      if (!stripe || !stripeElement) return;
      setIsProcessing(true);
      const { error } = await stripe.confirmPayment({
          elements: stripeElement,
          redirect: 'if_required'
      });

      if (error) {
          setPaymentError(error.message);
          setIsProcessing(false);
      } else {
          await finalizeBooking();
      }
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => { 
      document.body.style.overflow = 'auto'; 
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div 
      className="fixed inset-0 z-[500] flex items-center justify-center p-3 sm:p-4 md:p-6 bg-slate-900/50 backdrop-blur-md overflow-y-auto animate-in fade-in duration-300"
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-modal-title"
    >
      <div className="bg-white/95 backdrop-blur-xl w-full max-w-lg rounded-[2rem] sm:rounded-[2.5rem] md:rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[calc(100dvh-2rem)] sm:max-h-[85vh] flex flex-col my-auto border border-white/50 relative">
        <button 
          onClick={onClose} 
          className="absolute top-3 right-3 sm:top-5 sm:right-5 w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-800 transition-colors bg-white/80 rounded-2xl z-20 border border-slate-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          aria-label="Close appointment booking dialog"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true"></i>
        </button>

        <div className="p-5 pt-12 sm:p-6 md:p-8 border-b border-slate-100/50 bg-gradient-to-br from-indigo-50/80 to-white/80 text-center shrink-0">
          <h3 id="booking-modal-title" className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 mb-2 font-['Lora',_serif]">{step === 'date' ? 'Book Appointment' : 'Secure Checkout'}</h3>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 mb-3 sm:mb-4">
             <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" aria-hidden="true"></span>
             <p className="text-indigo-600 font-bold text-[10px] md:text-xs uppercase tracking-[0.2em]">{doctor.name.toLowerCase().startsWith('dr') ? doctor.name : `Dr. ${doctor.name}`}</p>
          </div>
          {step === 'date' && (
            <div className="flex flex-wrap justify-center gap-2 mt-1">
                <span className="bg-white/80 text-slate-500 px-3 py-1.5 rounded-xl border border-slate-100 text-[9px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1.5">
                   <i className="fa-solid fa-earth-africa text-indigo-400" aria-hidden="true"></i> Doctor: {doctorTimezone}
                </span>
                <span className="bg-white/80 text-slate-500 px-3 py-1.5 rounded-xl border border-slate-100 text-[9px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1.5">
                   <i className="fa-solid fa-user-clock text-indigo-400" aria-hidden="true"></i> Your Time: {userTimezone}
                </span>
            </div>
          )}
        </div>
        
        <div className="p-5 sm:p-6 md:p-8 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
          {paymentError && (
            <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-2xl flex items-center gap-2.5 text-rose-700 text-xs font-semibold animate-in fade-in" role="alert">
              <i className="fa-solid fa-circle-exclamation shrink-0 text-rose-500" aria-hidden="true"></i>
              <span>{paymentError}</span>
            </div>
          )}

          {step === 'date' ? (
              <>
                <div className="space-y-3">
                    <div className="flex items-center justify-between px-2">
                        <label htmlFor="appointment-date-input" className="text-[11px] font-black uppercase text-slate-400 tracking-[0.15em]">Preferred Date</label>
                        <span className="text-[11px] font-bold text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-md">{dayName}</span>
                    </div>
                    <div className="relative group">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400 group-focus-within:text-indigo-600 transition-colors pointer-events-none">
                            <i className="fa-solid fa-calendar-day" aria-hidden="true"></i>
                        </div>
                        <input 
                            id="appointment-date-input"
                            type="date" 
                            aria-label="Select appointment date"
                            className="w-full pl-11 pr-4 py-4 bg-slate-50 rounded-2xl border-2 border-slate-100/50 focus:border-indigo-500 focus:bg-white outline-none font-bold text-slate-700 transition-all text-sm md:text-base cursor-pointer shadow-sm focus:ring-2 focus:ring-indigo-500/20" 
                            value={date} 
                            min={getTodayDateString()} 
                            onChange={e => { setDate(e.target.value); setSelectedSlot(''); setPaymentError(''); }} 
                        />
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between px-2">
                      <label className="text-[11px] font-black uppercase text-slate-400 tracking-[0.15em]">Available Time Slots</label>
                      <div className="flex items-center gap-3 text-[9px] font-bold">
                        <span className="flex items-center gap-1 text-slate-500"><span className="w-2 h-2 rounded-full bg-indigo-500 inline-block" aria-hidden="true"></span> Open</span>
                        <span className="flex items-center gap-1 text-rose-600"><span className="w-2 h-2 rounded-full bg-rose-500 inline-block" aria-hidden="true"></span> Booked</span>
                        <span className="flex items-center gap-1 text-amber-700"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" aria-hidden="true"></span> Busy</span>
                      </div>
                    </div>

                    {isOff && customOpenSlots.size === 0 && (
                        <div className={`p-6 rounded-3xl text-center border mb-3 space-y-1 ${
                            allowSpecialRequests 
                                ? 'bg-amber-50/80 border-amber-200/80 text-amber-900' 
                                : 'bg-slate-50 border-slate-200/80 text-slate-700'
                        }`}>
                            <i className={`fa-solid ${allowSpecialRequests ? 'fa-calendar-plus text-amber-600' : 'fa-calendar-xmark text-slate-400'} text-xl mb-1`}></i>
                            <p className="font-bold text-xs">
                                Doctor is offline on {dayName}s
                            </p>
                            <p className="text-[11px] font-medium leading-relaxed">
                                {allowSpecialRequests 
                                    ? 'Special Requests are ENABLED for offline days. Choose any slot below to request an appointment.' 
                                    : 'Special requests are currently disabled for offline days. Please select an active working day.'}
                            </p>
                        </div>
                    )}

                    {isOff && customOpenSlots.size > 0 && (
                        <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200/60 text-center mb-3">
                            <p className="text-emerald-800 font-bold text-xs flex items-center justify-center gap-2">
                                <i className="fa-solid fa-calendar-check text-emerald-600"></i>
                                Custom active slots open for {date}
                            </p>
                        </div>
                    )}

                    {timeSlots.length > 0 && timeSlots.every(t => bookedSlots.has(normalizeTime(t)) || busySlots.has(normalizeTime(t))) && (
                        <div className="p-4 bg-rose-50 border border-rose-200/80 rounded-2xl text-center mb-3">
                            <p className="text-rose-800 font-bold text-xs flex items-center justify-center gap-2">
                                <i className="fa-solid fa-calendar-xmark text-rose-600"></i>
                                All slots for {date} are fully booked or unavailable. Please pick another date.
                            </p>
                        </div>
                    )}

                    {timeSlots.length === 0 ? (
                        !isOff && (
                            <div className="p-8 bg-slate-50 rounded-3xl text-center border border-slate-100 animate-in slide-in-from-top-2">
                                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">No active windows configured for this date.</p>
                            </div>
                        )
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" role="group" aria-label="Available appointment time slots">
                            {timeSlots.map(t => {
                                const normT = normalizeTime(t);
                                const isBooked = bookedSlots.has(normT);
                                const isBusy = busySlots.has(normT);
                                const isUnavailable = isBooked || isBusy;
                                const isSelected = selectedSlot === t || selectedSlot === normT;

                                return (
                                    <button 
                                        key={t} 
                                        type="button"
                                        disabled={isUnavailable}
                                        onClick={() => {
                                          if (!isUnavailable) {
                                            setSelectedSlot(t);
                                            setPaymentError('');
                                          }
                                        }} 
                                        aria-pressed={isSelected}
                                        aria-label={`Time slot ${t}, ${isBooked ? 'Booked' : isBusy ? 'Busy' : 'Available'}`}
                                        className={`py-3.5 px-3 rounded-2xl text-xs font-black transition-all duration-300 flex flex-col items-center justify-center gap-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                                            isBooked
                                            ? 'bg-rose-50 border-2 border-rose-200 text-rose-700 cursor-not-allowed opacity-90 shadow-2xs'
                                            : isBusy
                                            ? 'bg-amber-50 border-2 border-amber-200 text-amber-700 cursor-not-allowed opacity-90 shadow-2xs'
                                            : isSelected
                                            ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-600/20 -translate-y-1'
                                            : 'bg-white text-slate-700 border border-slate-200/80 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/30'
                                        }`}
                                    >
                                        <span className="text-sm font-black">{t}</span>
                                        {isBooked && (
                                          <span className="text-[8px] uppercase tracking-wider font-extrabold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                                            Booked
                                          </span>
                                        )}
                                        {isBusy && !isBooked && (
                                          <span className="text-[8px] uppercase tracking-wider font-extrabold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                                            Busy
                                          </span>
                                        )}
                                        {!isUnavailable && (
                                          <span className={`text-[8px] uppercase tracking-wider font-extrabold px-1.5 py-0.5 rounded ${isSelected ? 'bg-indigo-500 text-white' : 'bg-emerald-50 text-emerald-600'}`}>
                                            Open
                                          </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Patient Details for Guest / Anonymous Consultations */}
                {isGuestOrAnonymous && (
                  <div className="p-5 bg-gradient-to-br from-indigo-50/80 via-white to-teal-50/50 rounded-2xl border border-indigo-100 shadow-2xs space-y-3.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-black uppercase text-indigo-700 tracking-[0.15em] flex items-center gap-1.5">
                        <i className="fa-solid fa-envelope-circle-check text-indigo-600"></i> Confirmation Delivery Details
                      </span>
                      <span className="text-[9px] font-bold text-slate-400">Required</span>
                    </div>
                    <p className="text-xs text-slate-500 font-medium leading-relaxed">
                      Please enter your contact information to receive your official appointment confirmation and private video consultation link.
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Your Full Name</label>
                        <input 
                          type="text"
                          placeholder="e.g. Sarah Jenkins"
                          value={patientName}
                          onChange={e => { setPatientName(e.target.value); setPaymentError(''); }}
                          className="w-full px-4 py-3 bg-white rounded-xl border border-slate-200 font-bold text-slate-800 text-sm outline-none focus:border-indigo-500 shadow-2xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1">Your Email Address</label>
                        <input 
                          type="email"
                          placeholder="e.g. sarah.jenkins@example.com"
                          value={patientEmail}
                          onChange={e => { setPatientEmail(e.target.value); setPaymentError(''); }}
                          className="w-full px-4 py-3 bg-white rounded-xl border border-slate-200 font-bold text-slate-800 text-sm outline-none focus:border-indigo-500 shadow-2xs"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Consultation Rate summary */}
                <div className="p-5 bg-gradient-to-r from-slate-50 to-indigo-50/40 rounded-2xl border border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Consultation Fee</p>
                    <p className="text-xs font-bold text-slate-600">30-Minute Video Consultation</p>
                  </div>
                  <div className="text-right">
                    {priceInfo.isFree ? (
                      <div className="flex items-center gap-2">
                        {priceInfo.originalCutoff && (
                          <span className="text-xs font-bold text-slate-400 line-through">
                            {priceInfo.originalCutoff}
                          </span>
                        )}
                        <span className="text-base font-black text-emerald-600 bg-emerald-50 px-3 py-1 rounded-xl border border-emerald-200">
                          {priceInfo.displayPrice}
                        </span>
                      </div>
                    ) : (
                      <span className="text-base font-black text-indigo-600">
                        {priceInfo.displayPrice}
                      </span>
                    )}
                  </div>
                </div>
              </>
          ) : (
              <div className="space-y-8 animate-in slide-in-from-right-4 duration-500">
                  <div className="p-6 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-[2.5rem] flex justify-between items-center shadow-xl shadow-indigo-600/20 text-white relative overflow-hidden">
                      <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" aria-hidden="true"></div>
                      <div className="relative z-10">
                          <p className="text-[10px] font-black text-indigo-100 uppercase tracking-[0.2em] mb-1">Consultation Type</p>
                          <p className="font-bold text-white text-lg font-['Lora',_serif]">Online Video Session</p>
                      </div>
                      <div className="text-right relative z-10">
                        {priceInfo.isFree ? (
                          <div className="flex flex-col items-end">
                            {priceInfo.originalCutoff && (
                              <span className="text-xs font-bold text-indigo-200 line-through">
                                {priceInfo.originalCutoff}
                              </span>
                            )}
                            <p className="font-bold text-emerald-300 text-2xl leading-none font-['Lora',_serif]">
                              {priceInfo.displayPrice}
                            </p>
                          </div>
                        ) : (
                          <p className="font-bold text-white text-3xl leading-none font-['Lora',_serif]">
                              {priceInfo.displayPrice}
                          </p>
                        )}
                      </div>
                  </div>
                  
                  <div className="space-y-4">
                    <label className="text-[11px] font-black uppercase text-slate-400 tracking-[0.15em] px-2">Payment Method</label>
                    <div className="flex gap-3 p-1.5 bg-slate-100/50 rounded-[1.5rem] border border-slate-100" role="radiogroup" aria-label="Select Payment Method">
                        {globalSettings.stripeEnabled && (
                            <button 
                                onClick={() => setPaymentMethod('stripe')} 
                                aria-checked={paymentMethod === 'stripe'}
                                role="radio"
                                className={`flex-1 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                                    paymentMethod === 'stripe' 
                                    ? 'bg-white text-indigo-600 shadow-md' 
                                    : 'text-slate-400 hover:text-slate-600'
                                }`}
                            >
                                <i className="fa-solid fa-credit-card mr-2" aria-hidden="true"></i> Card
                            </button>
                        )}
                        {globalSettings.paypalEnabled && (
                            <button 
                                onClick={() => setPaymentMethod('paypal')} 
                                aria-checked={paymentMethod === 'paypal'}
                                role="radio"
                                className={`flex-1 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                                    paymentMethod === 'paypal' 
                                    ? 'bg-white text-indigo-600 shadow-md' 
                                    : 'text-slate-400 hover:text-slate-600'
                                }`}
                            >
                                <i className="fa-brands fa-paypal mr-2" aria-hidden="true"></i> PayPal
                            </button>
                        )}
                    </div>
                  </div>

                  {paymentMethod === 'stripe' && globalSettings.stripeEnabled && (
                      <div className="space-y-4 pt-2">
                          <div id="card-element" className="p-4 bg-white rounded-2xl border-2 border-slate-100 min-h-[50px] shadow-sm shadow-slate-100/50"></div>
                          {paymentError && <p className="text-[10px] font-black text-rose-500 bg-rose-50 p-3 rounded-xl border border-rose-100 italic" role="alert">"{paymentError}"</p>}
                          <Button onClick={handleStripePay} disabled={isProcessing} ariaLabel="Complete Secure Payment" className="w-full py-5 text-sm shadow-xl shadow-indigo-100">Complete Secure Payment</Button>
                      </div>
                  )}

                  {paymentMethod === 'paypal' && globalSettings.paypalEnabled && (
                      <div id="paypal-button-container" className="min-h-[150px] pt-2"></div>
                  )}
              </div>
          )}
        </div>
        
        <div className="p-6 md:p-10 bg-slate-50/50 flex gap-3 md:gap-4 shrink-0 border-t border-slate-100">
          <Button onClick={onClose} variant="ghost" ariaLabel="Cancel booking" className="flex-1 py-4">Cancel</Button>
          {step === 'date' && (
              <Button onClick={handleNext} disabled={!selectedSlot} ariaLabel="Confirm selected time slot" className="flex-[2] py-4 shadow-lg shadow-indigo-500/20">Confirm Time Slot</Button>
          )}
        </div>
      </div>
    </div>
  );
};

const DoctorDetailModal = ({ doctor, onClose, onBookClick }: { doctor: User, onClose: () => void, onBookClick: () => void }) => {
  const { reviews, globalSettings, formatDoctorPrice } = useApp();
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const priceInfo = formatDoctorPrice(doctor.sessionRate);
  const docReviews = reviews.filter(r => r.doctorId === doctor.id);
  const avgRating = docReviews.length 
    ? (docReviews.reduce((sum, r) => sum + r.rating, 0) / docReviews.length).toFixed(1) 
    : "New";

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => { 
      document.body.style.overflow = 'auto'; 
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div 
      className="fixed inset-0 z-[500] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto animate-in fade-in duration-300"
      role="dialog"
      aria-modal="true"
      aria-labelledby="doctor-detail-heading"
    >
      <div className="bg-white w-full max-w-4xl rounded-[1.8rem] sm:rounded-[2.5rem] md:rounded-[3rem] shadow-2xl animate-in zoom-in-95 duration-300 relative max-h-[calc(100dvh-1.25rem)] sm:max-h-[calc(100dvh-2rem)] md:max-h-[85vh] my-auto flex flex-col overflow-hidden border border-slate-100">
        
        {/* Top Header Banner with Doctor Details integrated cleanly */}
        <div className="relative bg-gradient-to-br from-slate-900 via-indigo-950 to-indigo-900 p-5 pt-12 sm:p-6 md:p-8 text-white shrink-0 overflow-hidden">
          <div className="absolute inset-0 opacity-15 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" aria-hidden="true"></div>
          <div className="absolute -top-10 -right-10 w-60 h-60 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" aria-hidden="true"></div>
          <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-teal-500/20 rounded-full blur-3xl pointer-events-none" aria-hidden="true"></div>

          {/* Modal Close Button - positioned inside top right with clear aria-label and padding clear */}
          <button 
            onClick={onClose} 
            className="absolute top-3 right-3 sm:top-5 sm:right-5 w-10 h-10 flex items-center justify-center text-slate-300 hover:text-white transition-all bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-2xl z-30 border border-white/20 group active:scale-95 focus:outline-none focus:ring-2 focus:ring-white"
            aria-label={`Close modal profile for ${doctor.name}`}
          >
            <i className="fa-solid fa-xmark text-lg group-hover:rotate-90 transition-transform" aria-hidden="true"></i>
          </button>

          {/* Integrated Doctor Header Row with guaranteed clear space */}
          <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-6 text-center sm:text-left w-full sm:pr-12">
            <div className="relative group shrink-0">
              <img 
                src={doctor.avatar} 
                className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-[1.5rem] sm:rounded-[2rem] bg-white/10 border-3 sm:border-4 border-white/20 shadow-2xl object-cover" 
                alt={`Portrait of ${doctor.name}`} 
                loading="eager" 
              />
              {doctor.isVerified && (
                <div className="absolute -bottom-1 -right-1 bg-white p-0.5 rounded-full shadow-md" title="Verified Medical Professional">
                  <div className="bg-indigo-600 text-white w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center border-2 border-white text-[10px] sm:text-xs">
                    <i className="fa-solid fa-check" aria-hidden="true"></i>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0 w-full">
              <div className="inline-flex items-center gap-1.5 px-3 py-0.5 sm:py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-200 text-[9px] sm:text-[10px] font-black uppercase tracking-widest mb-1.5">
                <i className="fa-solid fa-user-doctor text-indigo-300" aria-hidden="true"></i> Board Certified Specialist
              </div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h3 id="doctor-detail-heading" className="text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight font-['Lora',_serif] break-words leading-tight">
                  {doctor.name.toLowerCase().startsWith('dr') ? doctor.name : `Dr. ${doctor.name}`}
                </h3>
                <ParticipantStatusBadge user={doctor} />
              </div>
              <div className="flex flex-wrap justify-center sm:justify-start gap-1.5 sm:gap-2 mt-2">
                {doctor.specialties?.length ? doctor.specialties.map(s => (
                  <span key={s} className="text-[9px] sm:text-[10px] md:text-xs font-bold uppercase tracking-wider text-indigo-100 bg-white/15 px-3 py-0.5 sm:px-3.5 sm:py-1 rounded-xl border border-white/10 backdrop-blur-md">
                    {s}
                  </span>
                )) : (
                  <span className="text-[9px] sm:text-[10px] md:text-xs font-bold uppercase tracking-wider text-indigo-200 bg-white/10 px-3 py-0.5 sm:px-3.5 sm:py-1 rounded-xl border border-white/10">
                    General Physician
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 scrollbar-thin scroll-smooth min-h-0 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            <div className="md:col-span-2 space-y-5">
              {/* Clinical Profile */}
              <div className="bg-slate-50/80 p-5 sm:p-6 rounded-2xl sm:rounded-3xl border border-slate-100 space-y-2.5">
                <div className="flex items-center gap-2.5">
                  <div className="w-1.5 h-4 bg-indigo-600 rounded-full" aria-hidden="true"></div>
                  <h4 className="text-[11px] sm:text-xs font-bold uppercase text-slate-500 tracking-wider">Clinical Background & Experience</h4>
                </div>
                <p id="doctor-modal-bio" className="text-slate-700 font-medium leading-relaxed text-xs sm:text-sm md:text-base italic whitespace-pre-line">
                  {doctor.bio || "Dedicated healthcare professional providing personalized telemedicine consultations, comprehensive clinical evaluations, and evidence-based treatment plans."}
                </p>
              </div>

              {/* Patient Reviews */}
              <div className="space-y-4">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2.5">
                    <div className="w-1.5 h-5 bg-indigo-600 rounded-full" aria-hidden="true"></div>
                    <h4 className="text-xs font-bold uppercase text-slate-500 tracking-wider">Verified Patient Reviews ({docReviews.length})</h4>
                  </div>
                  <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-3 py-0.5 rounded-full border border-indigo-100">
                    {docReviews.length} Verified
                  </span>
                </div>
                
                {docReviews.length === 0 ? (
                  <div className="p-6 md:p-8 bg-slate-50/50 rounded-3xl text-center border-2 border-dashed border-slate-200/80 space-y-2">
                    <i className="fa-solid fa-star-half-stroke text-slate-300 text-2xl mb-1 block" aria-hidden="true"></i>
                    <p className="text-slate-600 font-semibold text-sm">
                      New Specialist — No patient reviews yet.
                    </p>
                    <p className="text-slate-400 text-xs font-medium">
                      Experience their clinical care firsthand and be among the first to share your feedback following your consultation.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {docReviews.map(r => (
                      <div key={r.id} className="p-5 bg-white rounded-2xl border border-slate-100 shadow-sm hover:border-slate-200 transition-all">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center font-bold text-xs" aria-hidden="true">
                              {r.patientName[0]}
                            </div>
                            <p className="font-bold text-slate-900 text-sm">{r.patientName}</p>
                          </div>
                          <div className="flex gap-0.5 text-amber-400 text-xs" aria-label={`Rating: ${r.rating} out of 5 stars`}>
                            {[...Array(5)].map((_, i) => (
                              <i key={i} className={`fa-solid fa-star ${i < r.rating ? 'text-amber-400' : 'text-slate-200'}`} aria-hidden="true"></i>
                            ))}
                          </div>
                        </div>
                        <p className="text-slate-600 text-xs md:text-sm leading-relaxed italic pl-3 border-l-2 border-indigo-100 ml-3">
                          "{r.comment}"
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Consultation Window */}
              {doctor.workingHours && Object.values(doctor.workingHours).some(d => d.active) && (
                <div className="bg-indigo-50/40 p-6 md:p-8 rounded-3xl border border-indigo-100/60 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-1.5 h-5 bg-indigo-600 rounded-full" aria-hidden="true"></div>
                      <h4 className="text-xs font-bold uppercase text-indigo-900/70 tracking-wider">Weekly Schedule & Timezone</h4>
                    </div>
                    <span className="text-[11px] font-bold text-indigo-700 bg-white px-3 py-1 rounded-xl shadow-sm border border-indigo-100 uppercase tracking-wider">
                      {doctor.timezone || 'UTC'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                    {days.map(day => {
                      const schedule = doctor.workingHours?.[day];
                      if (!schedule?.active) return null;
                      return (
                        <div key={day} className="flex justify-between items-center text-xs py-2 border-b border-indigo-100/40">
                          <span className="font-semibold text-slate-700">{day}</span>
                          <div className="flex items-center gap-1.5 font-bold text-indigo-800">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" aria-hidden="true"></span>
                            <span>{schedule.start} — {schedule.end}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Side Booking & Stats */}
            <div className="space-y-5">
              <div className="bg-gradient-to-br from-indigo-700 via-indigo-800 to-slate-900 p-6 md:p-8 rounded-3xl text-center shadow-xl shadow-indigo-950/10 text-white relative overflow-hidden">
                <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center text-white text-xl mx-auto mb-4 border border-white/20" aria-hidden="true">
                  <i className="fa-solid fa-calendar-check"></i>
                </div>
                <h5 className="font-bold text-white mb-1 text-lg font-['Lora',_serif]">Direct Appointment</h5>
                <p className="text-xs text-indigo-200 font-medium mb-6">Schedule your one-on-one virtual consultation.</p>
                
                <button 
                  onClick={onBookClick} 
                  aria-label={`Book appointment with ${doctor.name}`}
                  className="w-full py-3.5 px-6 rounded-2xl text-sm font-black bg-white text-indigo-900 hover:bg-indigo-50 active:scale-95 shadow-xl transition-all flex items-center justify-center gap-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white"
                >
                  <i className="fa-regular fa-clock text-indigo-600" aria-hidden="true"></i>
                  <span>Check Availability</span>
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-1 gap-3">
                <div className="bg-slate-50/80 p-5 rounded-2xl text-center border border-slate-200/60 shadow-sm">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Consultation Fee</p>
                  <div className="flex flex-col items-center gap-0.5">
                    {priceInfo.isFree ? (
                      <>
                        <span className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded uppercase tracking-wider mb-0.5">
                          Complimentary
                        </span>
                        {priceInfo.originalCutoff && (
                          <span className="text-xs font-bold text-slate-400 line-through">
                            {priceInfo.originalCutoff}
                          </span>
                        )}
                        <p className="font-bold text-emerald-600 text-xl md:text-2xl font-['Lora',_serif]">
                          {priceInfo.displayPrice}
                        </p>
                      </>
                    ) : (
                      <p className="font-bold text-slate-900 text-xl md:text-2xl font-['Lora',_serif]">
                        {priceInfo.displayPrice}
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-slate-50/80 p-5 rounded-2xl text-center border border-slate-200/60 shadow-sm">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Doctor Rating</p>
                  <div className="flex justify-center items-center gap-1.5 text-amber-500 text-xl font-bold">
                    {avgRating === "New" ? (
                      <span className="text-slate-400 font-bold text-xs uppercase tracking-wider bg-white px-2.5 py-1 rounded-lg border border-slate-200">
                        New Specialist
                      </span>
                    ) : (
                      <>
                        <span className="text-slate-900 font-['Lora',_serif]">{avgRating}</span>
                        <i className="fa-solid fa-star text-sm" aria-hidden="true"></i>
                      </>
                    )}
                  </div>
                  <p className="text-[10px] font-medium text-slate-400 mt-1.5">{docReviews.length} Verified Ratings</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SpecialistsPage: React.FC<{ onBook: (id: string, date: string, time: string, timezone: string, isFree?: boolean, paymentInfo?: any) => Promise<void> }> = ({ onBook }) => {
  const { doctors, doctorsError, loading, globalSettings, formatDoctorPrice, userCurrency, userCountry, setUserCurrency } = useApp();
  const [filter, setFilter] = useState('All');
  const [selectedDoctorForBooking, setSelectedDoctorForBooking] = useState<User | null>(null);
  const [selectedDoctorForDetail, setSelectedDoctorForDetail] = useState<User | null>(null);
  
  // Auto-dismiss open modals if the selected specialist was disabled or suspended by admin
  useEffect(() => {
    if (selectedDoctorForDetail && isUserDisabled(selectedDoctorForDetail)) {
      setSelectedDoctorForDetail(null);
    }
    if (selectedDoctorForBooking && isUserDisabled(selectedDoctorForBooking)) {
      setSelectedDoctorForBooking(null);
    }
  }, [doctors, selectedDoctorForDetail, selectedDoctorForBooking]);

  const categories = [
    'All', 
    'General Physician', 
    'Medicine',
    'Internal Medicine',
    'Nephrology', 
    'Cardiology', 
    'Neurology', 
    'Dentist'
  ];

  const filteredDoctors = doctors.filter(d => {
    if (isUserDisabled(d)) return false;
    if (filter === 'All') return true;
    const specialties = Array.isArray(d.specialties) ? d.specialties : [];
    return specialties.includes(filter);
  });

  return (
    <div className="py-16 max-w-7xl mx-auto px-4 animate-in fade-in duration-700">
      <SectionTitle title="Find Your Doctor" subtitle="Consult with our board-certified doctors from the comfort of your home." centered />
      
      {/* Location & Currency indicator bar */}
      <div className="max-w-xl mx-auto mb-8 bg-slate-50/80 backdrop-blur-md p-3 rounded-2xl border border-slate-200/80 flex items-center justify-between text-xs shadow-sm">
        <div className="flex items-center gap-2 pl-2">
          <i className="fa-solid fa-location-dot text-indigo-500" aria-hidden="true"></i>
          <span className="text-slate-600 font-medium">Location: <strong className="text-slate-900">{userCountry}</strong></span>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="specialists-currency-select" className="text-slate-500 font-medium">Currency:</label>
          <select 
            id="specialists-currency-select"
            value={userCurrency} 
            onChange={e => setUserCurrency(e.target.value)}
            aria-label="Select preferred display currency"
            className="bg-white border border-slate-200 rounded-xl px-2.5 py-1 font-bold text-slate-800 text-xs shadow-sm cursor-pointer outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
          >
            {Object.entries(CURRENCIES).map(([code, info]) => (
              <option key={code} value={code}>
                {info.symbol} ({code}) - {info.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {doctorsError && doctors.length === 0 && (
        <div role="alert" className="max-w-2xl mx-auto mb-16 p-8 bg-rose-50 border-2 border-rose-100 rounded-[2.5rem] text-center space-y-4 animate-in shake duration-500">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-rose-500 text-2xl mx-auto shadow-sm" aria-hidden="true">
            <i className="fa-solid fa-triangle-exclamation"></i>
          </div>
          <h3 className="text-xl font-black text-rose-900 tracking-tight">Doctor System Alert</h3>
          <p className="text-rose-600 font-medium text-sm leading-relaxed">
            We are unable to reach our live doctor registry at the moment. Please try again in a few minutes.
          </p>
          <div className="p-4 bg-white/50 rounded-xl text-[10px] font-black uppercase text-rose-400 tracking-widest text-center">Service Status: Offline</div>
        </div>
      )}

      {doctorsError && doctors.length > 0 && (
        <div className="max-w-xl mx-auto mb-8 px-5 py-2.5 bg-amber-50 border border-amber-200 rounded-2xl flex items-center justify-center gap-3 text-xs font-semibold text-amber-800 shadow-sm animate-in fade-in">
          <i className="fa-solid fa-cloud-arrow-down text-amber-500 text-sm shrink-0" aria-hidden="true"></i>
          <span>Live registry offline. Displaying verified local specialist roster.</span>
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-4 mb-20" role="tablist" aria-label="Filter doctors by specialty">
        {categories.map(cat => (
          <button 
            key={cat} 
            onClick={() => setFilter(cat)} 
            role="tab"
            aria-selected={filter === cat}
            aria-label={`Filter by specialty ${cat}`}
            className={`px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all duration-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                filter === cat 
                ? 'bg-indigo-600 text-white shadow-2xl shadow-indigo-500/30 -translate-y-1' 
                : 'bg-white text-slate-400 border border-slate-100 hover:border-indigo-200'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
        {filteredDoctors.map(doc => {
          const price = formatDoctorPrice(doc.sessionRate);
          const docNameFormatted = doc.name.toLowerCase().startsWith('dr') ? doc.name : `Dr. ${doc.name}`;
          return (
            <div 
              key={doc.id} 
              onClick={() => setSelectedDoctorForDetail(doc)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedDoctorForDetail(doc);
                }
              }}
              role="article"
              tabIndex={0}
              aria-label={`Medical doctor card for ${docNameFormatted}. Click to view full clinical profile.`}
              className="bg-white p-8 md:p-10 rounded-[3rem] md:rounded-[4rem] border-2 border-transparent hover:border-indigo-100 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-500 group cursor-pointer flex flex-col h-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <div className="flex items-center gap-5 md:gap-6 mb-8">
                <div className="relative shrink-0">
                  <img src={doc.avatar} className="w-20 h-20 rounded-3xl bg-slate-50 border-4 border-white shadow-lg group-hover:rotate-3 transition-transform duration-500 object-cover" alt={`Portrait of ${docNameFormatted}`} loading="lazy" />
                  <span className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 border-4 border-white rounded-full" title="Active Specialist" aria-hidden="true"></span>
                </div>
                <div className="flex-grow min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-xl font-black text-slate-900 leading-tight break-words">{docNameFormatted}</h3>
                      {doc.isVerified && <i className="fa-solid fa-circle-check text-indigo-500 text-lg shrink-0" title="Verified Professional" aria-label="Verified Professional"></i>}
                      <ParticipantStatusBadge user={doc} compact />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                      {doc.specialties?.length ? (
                          <>
                              <span className="text-[8px] font-black uppercase tracking-widest text-indigo-500 bg-indigo-50 px-3 py-1 rounded-lg">
                                  {doc.specialties[0]}
                              </span>
                              {doc.specialties.length > 1 && (
                                  <span className="text-[8px] font-black text-slate-400 bg-slate-50 px-3 py-1 rounded-lg" title={doc.specialties.slice(1).join(', ')}>
                                      +{doc.specialties.length - 1} more
                                  </span>
                              )}
                          </>
                      ) : (
                          <span className="text-[8px] font-black uppercase tracking-widest text-slate-400 bg-slate-50 px-3 py-1 rounded-lg">General Physician</span>
                      )}
                  </div>
                </div>
              </div>

              <p className="text-slate-700 text-base md:text-lg font-medium mb-8 leading-relaxed italic flex-grow line-clamp-3">
                  {doc.bio || "Dedicated healthcare provider focusing on personalized telemedicine solutions and advanced clinical outcomes."}
              </p>

              <div className="flex items-center justify-between pt-6 border-t border-slate-50">
                <div className="flex flex-col">
                  {price.isFree ? (
                    <div className="flex flex-col">
                      <span className="text-[7px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 px-1.5 py-0.5 rounded w-fit mb-1">Complimentary Access</span>
                      <div className="flex items-center gap-1.5">
                        {price.originalCutoff && (
                          <span className="text-xs font-bold text-slate-400 line-through">
                            {price.originalCutoff}
                          </span>
                        )}
                        <span className="text-sm font-black text-emerald-600">
                          {price.displayPrice}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">Fee per visit</span>
                      <span className="text-sm font-black text-slate-900">
                        {price.displayPrice}
                      </span>
                    </div>
                  )}
                  <div className="text-indigo-600 font-black text-[9px] uppercase tracking-widest group-hover:translate-x-1 transition-transform mt-1">
                      View Profile <i className="fa-solid fa-arrow-right ml-1" aria-hidden="true"></i>
                  </div>
                </div>
                <Button 
                  variant="outline"
                  className="px-8 py-2.5"
                  ariaLabel={`Book appointment with ${docNameFormatted}`}
                  onClick={(e) => {
                      if (e) e.stopPropagation();
                      setSelectedDoctorForBooking(doc);
                  }}
                >
                  Book
                </Button>
              </div>
            </div>
          );
        })}

        {!loading && filteredDoctors.length === 0 && !doctorsError && (
          <div className="col-span-full py-32 text-center bg-slate-50/50 rounded-[4rem] border-2 border-dashed border-slate-100">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-slate-200 text-3xl mx-auto mb-6" aria-hidden="true"><i className="fa-solid fa-user-doctor"></i></div>
            <p className="text-slate-300 italic font-black uppercase text-[10px] tracking-widest">No doctors found in the {filter} category.</p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedDoctorForDetail && (
        <DoctorDetailModal 
            doctor={selectedDoctorForDetail} 
            onClose={() => setSelectedDoctorForDetail(null)} 
            onBookClick={() => {
                const doc = selectedDoctorForDetail;
                setSelectedDoctorForDetail(null);
                setSelectedDoctorForBooking(doc);
            }}
        />
      )}

      {/* Booking Modal */}
      {selectedDoctorForBooking && (
        <BookingModal 
          doctor={selectedDoctorForBooking} 
          onClose={() => setSelectedDoctorForBooking(null)} 
          onConfirm={async (date, time, timezone, isFree, paymentInfo) => { 
            const docId = selectedDoctorForBooking.id;
            setSelectedDoctorForBooking(null); 
            await onBook(docId, time, date, timezone, isFree, paymentInfo); 
          }} 
        />
      )}
      
      <ScrollNavigation nextId="footer" prevId="hero" isLast />
    </div>
  );
};